import React, { useMemo } from 'react';
import { Card, Box, Typography, Stack, Avatar, Tabs, Tab, Chip, alpha, useTheme } from '@mui/material';
import { User, ShoppingBag, Briefcase, MessageSquare } from 'lucide-react';

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - d);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const RecentActivityCard = ({ recent = {} }) => {
  const theme = useTheme();
  const [tab, setTab] = React.useState(0);
  const tabs = useMemo(
    () => [
      {
        key: 'users',
        label: 'Users',
        icon: <User size={16} />,
        color: theme.palette.primary.main,
        items: recent.users || [],
        primary: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'User',
        secondary: (r) => r.email,
      },
      {
        key: 'products',
        label: 'Products',
        icon: <ShoppingBag size={16} />,
        color: theme.palette.secondary.main,
        items: recent.products || [],
        primary: (r) => r.product_name,
        secondary: (r) => r.city,
      },
      {
        key: 'businesses',
        label: 'Businesses',
        icon: <Briefcase size={16} />,
        color: theme.palette.warning.main,
        items: recent.businesses || [],
        primary: (r) => r.business_name,
        secondary: (r) => r.city,
      },
      {
        key: 'enquiries',
        label: 'Leads',
        icon: <MessageSquare size={16} />,
        color: theme.palette.error.main,
        items: recent.enquiries || [],
        primary: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || String(r.phone || 'Lead'),
        secondary: (r) => `${r.phone ? r.phone + ' · ' : ''}${r.city || ''}`,
      },
    ],
    [recent, theme]
  );
  const active = tabs[tab];

  return (
    <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, height: '100%', width: '100%', maxWidth: '100%' }}>
      <Box mb={1.5}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Recent Activity</Typography>
        <Typography variant="body2" color="text.secondary">
          Latest entities on the platform
        </Typography>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 38,
          mb: 1,
          '& .MuiTabs-indicator': { height: 2.5, borderRadius: 2 },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 700,
            minHeight: 38,
            px: 1.5,
            fontSize: 13,
          },
        }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.key}
            iconPosition="start"
            icon={t.icon}
            label={`${t.label} (${t.items.length})`}
          />
        ))}
      </Tabs>
      <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
        {active.items.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No recent records</Typography>
          </Box>
        )}
        <Stack spacing={0.75}>
          {active.items.map((row, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                p: 1.25,
                borderRadius: 2,
                transition: 'background 120ms',
                '&:hover': { bgcolor: alpha(active.color, 0.06) },
              }}
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: alpha(active.color, 0.14),
                  color: active.color,
                  fontSize: 14,
                  fontWeight: 800,
                }}
              >
                {(active.primary(row) || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                  {active.primary(row)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {active.secondary(row)}
                </Typography>
              </Box>
              <Chip
                label={timeAgo(row.created_at)}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.grey[500], 0.10),
                  color: 'text.secondary',
                }}
              />
            </Stack>
          ))}
        </Stack>
      </Box>
    </Card>
  );
};

export default React.memo(RecentActivityCard);
